import React from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { useTheme } from "styled-components";
import { formatarMoeda } from "../../../utils/calculos";
import { usePrecosVisiveis } from "../../../context/PrecosContext";
import {
  ChartWrapper,
  ProgressFill,
  ProgressTrack,
  StackedCard,
  StackedLabel,
  StackedSub,
  StackedValue,
  ToneText,
} from "./styles";

function tomDoProgresso(pct) {
  if (pct == null) return "neutral";
  if (pct >= 1) return "success";
  if (pct >= 0.7) return "warning";
  return "danger";
}

export default function ThroughputCard({ throughput, burndown, metaAmbientes }) {
  const { precosVisiveis } = usePrecosVisiveis();
  const theme = useTheme();
  const pct = throughput.pctMetaGanho;
  const tone = tomDoProgresso(pct);

  const ganhoText = precosVisiveis
    ? formatarMoeda(throughput.ganho)
    : "••••••";
  const metaText = precosVisiveis
    ? formatarMoeda(throughput.metaGanhoRS)
    : "••••••";

  const ritmoText =
    throughput.ritmoNecessarioRS != null
      ? `${precosVisiveis ? formatarMoeda(throughput.ritmoNecessarioRS) : "••••••"}/du`
      : "—";
  const ritmoAmbText =
    throughput.ritmoAmbientes != null
      ? `${throughput.ritmoAmbientes.toFixed(1)} amb/du`
      : "—";

  const dadosChart = (burndown?.pontos || []).map((p) => ({
    dia: p.data.getDate(),
    acumulado: p.acumulado,
  }));

  return (
    <StackedCard $tone="default" aria-label="Throughput do mês">
      <StackedLabel>Throughput (ganho do mês)</StackedLabel>
      <StackedValue>
        {ganhoText}
        {throughput.metaGanhoRS > 0 && (
          <ToneText $tone={tone} style={{ fontSize: "0.7em", marginLeft: 8 }}>
            {pct != null ? `${Math.round(pct * 100)}%` : ""}
          </ToneText>
        )}
      </StackedValue>
      {throughput.metaGanhoRS > 0 ? (
        <>
          <ProgressTrack aria-hidden>
            <ProgressFill $pct={pct ?? 0} $tone={tone} />
          </ProgressTrack>
          <StackedSub>
            Meta {metaText} · Ritmo necessário {ritmoText}
            {metaAmbientes > 0 ? ` · ${ritmoAmbText}` : ""}
          </StackedSub>
        </>
      ) : (
        <StackedSub>
          {throughput.qtdEntregue} ambientes entregues no mês ·{" "}
          Defina meta nos parâmetros para acompanhar ritmo.
        </StackedSub>
      )}
      {dadosChart.length > 0 && (
        <ChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={dadosChart}
              margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="ttp-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.accent} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={theme.accent} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="dia" hide />
              <YAxis hide />
              <Tooltip
                cursor={{ stroke: theme.border }}
                contentStyle={{
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  fontSize: 12,
                  borderRadius: 8,
                }}
                labelFormatter={(dia) => `Dia ${dia}`}
                formatter={(value) => [value, "Acumulado"]}
              />
              <Area
                type="monotone"
                dataKey="acumulado"
                stroke={theme.accent}
                fill="url(#ttp-grad)"
                strokeWidth={2}
                connectNulls={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartWrapper>
      )}
    </StackedCard>
  );
}
